import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CalendarRange } from "lucide-react";
import useReveal from "@/hooks/useReveal";
import useWeeklyTheme from "@/hooks/useWeeklyTheme";
import { track } from "@/lib/analytics";

/**
 * TemaDaSemanaHoje, apresenta o tema semanal ativo com foco no dia de hoje.
 * Silencia (retorna null) quando não há tema semanal ativo.
 * CTA: "Ver a semana completa" → /semana/{slug}
 */
export default function TemaDaSemanaHoje() {
  const { theme } = useWeeklyTheme();
  const r = useReveal();

  if (!theme || !theme.theme_name) return null;

  const accent = theme.accent_color || "#C5A059";
  const slug = theme.theme_slug;

  return (
    <section
      id="tema-da-semana"
      data-testid="tema-semana-section"
      className="relative py-16 md:py-20 border-t border-[#0B1E36]/6"
      style={{
        backgroundImage: `linear-gradient(180deg, #F7F5F0 0%, #FFFFFF 100%)`,
      }}
    >
      <div className="max-w-5xl mx-auto px-6 md:px-8">
        <div ref={r} className="reveal">
          <div className="flex items-center gap-3 mb-6">
            <CalendarRange size={14} strokeWidth={1.6} style={{ color: accent }} />
            <span
              className="text-[0.68rem] uppercase tracking-[0.32em] font-medium"
              style={{ color: accent }}
            >
              {theme.is_liturgical ? "Semana litúrgica" : "Tema desta semana"}
            </span>
            {theme.is_liturgical && (
              <span
                data-testid="tema-semana-liturgical-badge"
                className="text-[0.55rem] uppercase tracking-[0.28em] font-medium px-2 py-0.5 border"
                style={{ color: accent, borderColor: `${accent}55`, background: `${accent}12` }}
              >
                Calendário cristão
              </span>
            )}
          </div>

          <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-start">
            <div className="md:col-span-7">
              <h2
                data-testid="tema-semana-title"
                className="font-serif-display text-[#0B1E36] text-[2rem] md:text-[2.75rem] lg:text-[3.25rem] leading-[1.05] tracking-[-0.024em] font-medium"
              >
                {theme.theme_name}
              </h2>
              {theme.theme_description && (
                <p
                  data-testid="tema-semana-descr"
                  className="mt-5 text-[1rem] md:text-[1.1rem] text-[#1F1A17]/70 leading-[1.8] font-light max-w-xl"
                >
                  {theme.theme_description}
                </p>
              )}

              {slug && (
                <Link
                  to={`/semana/${slug}`}
                  data-testid="tema-semana-cta"
                  onClick={() =>
                    track("weekly_theme_open", {
                      slug,
                      source: "home_tema_semana",
                    })
                  }
                  className="mt-8 inline-flex items-center gap-3 text-[0.72rem] uppercase tracking-[0.26em] text-[#0B1E36] hover:text-[#C5A059] border-b border-[#0B1E36]/25 hover:border-[#C5A059] pb-1.5 transition-colors"
                >
                  Ver a semana completa
                  <ArrowUpRight size={13} strokeWidth={1.6} />
                </Link>
              )}
            </div>

            {theme.verse_reference && (
              <aside
                className="md:col-span-5 border-l pl-6 md:pl-8"
                style={{ borderColor: `${accent}55` }}
                data-testid="tema-semana-verse"
              >
                <p className="text-[0.62rem] uppercase tracking-[0.3em] text-[#0B1E36]/50 font-medium mb-3">
                  Versículo âncora
                </p>
                {theme.verse_text && (
                  <p className="font-serif-display italic text-[1.15rem] md:text-[1.3rem] text-[#0B1E36]/90 leading-[1.55]">
                    &ldquo;{theme.verse_text}&rdquo;
                  </p>
                )}
                <p
                  className="mt-3 text-[0.7rem] uppercase tracking-[0.28em] font-medium"
                  style={{ color: accent }}
                >
                  {theme.verse_reference}
                </p>
              </aside>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
